import { FC, useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Badge, Box, Button, IconButton, Tooltip } from '@mui/material';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import GroupIcon from '@mui/icons-material/Group';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { PLATFORM_TOKEN_KEY, platformApi } from './platformApi';
import PlatformHeader, { HEADER_HEIGHT, PageTitleContext } from './PlatformHeader';

export interface IPlatformAdmin { _id: string; name: string; email: string; role: 'owner' | 'staff'; isActive: boolean; lastLoginAt?: string }

const MENU = [
  { to: '/platform', label: 'Dashboard', icon: <DashboardIcon fontSize='small' />, end: true },
  { to: '/platform/accounts', label: 'Accounts', icon: <BusinessIcon fontSize='small' /> },
  { to: '/platform/requests', label: 'Requests', icon: <MarkEmailUnreadIcon fontSize='small' /> },
  { to: '/platform/notifications', label: 'Notifications', icon: <NotificationsIcon fontSize='small' /> },
  { to: '/platform/plans', label: 'Plans', icon: <LocalOfferIcon fontSize='small' /> },
  { to: '/platform/team', label: 'Team', icon: <GroupIcon fontSize='small' /> },
];

const NAV_OPEN_KEY = 'platformNavOpen';
// fired after a request is updated so the menu badge refreshes
export const REQUESTS_CHANGED_EVENT = 'platform:requests-changed';

// remembered per browser; storage can be blocked, so never fail on it
const readNavOpen = () => {
  try { return localStorage.getItem(NAV_OPEN_KEY) === 'true'; } catch { return false; }
};

const PlatformLayout: FC = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState<IPlatformAdmin>();
  const [open, setOpen] = useState(readNavOpen);
  const token = localStorage.getItem(PLATFORM_TOKEN_KEY);
  const [pageTitle, setPageTitle] = useState('');
  const [openRequests, setOpenRequests] = useState(0);
  const location = useLocation();

  // open customer requests, shown as a badge on the Requests menu
  useEffect(() => {
    if (!token) return undefined;
    const refresh = () => platformApi.get('/requests?status=open').then((response) => setOpenRequests(response.data.open || 0)).catch(() => undefined);
    refresh();
    window.addEventListener(REQUESTS_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(REQUESTS_CHANGED_EVENT, refresh);
  }, [token, location.pathname]);

  const toggleNav = () => {
    setOpen(!open);
    try { localStorage.setItem(NAV_OPEN_KEY, String(!open)); } catch { /* ignore */ }
  };

  useEffect(() => {
    let active = true;
    if (token) {
      platformApi.get('/auth/me').then((response) => { if (active) setAdmin(response.data.admin); }).catch(() => undefined);
    }
    return () => { active = false; };
  }, [token]);

  if (!token) {
    return <Navigate to='/platform/login' replace />;
  }

  const logout = () => {
    localStorage.removeItem(PLATFORM_TOKEN_KEY);
    navigate('/platform/login', { replace: true });
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#fafafb' }}>
      <Box sx={{ width: open ? 210 : 60, transition: 'width 150ms', flexShrink: 0, bgcolor: '#fff', borderRight: '1px solid #dcdcdc', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '100vh', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: open ? 'space-between' : 'center', px: open ? 2 : 0, height: HEADER_HEIGHT, boxSizing: 'border-box', flexShrink: 0, borderBottom: '1px solid #dcdcdc' }}>
          {open && <span style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap' }}>Control panel</span>}
          <Tooltip title={open ? 'Collapse menu' : 'Open menu'} placement='right'>
            <IconButton size='small' onClick={toggleNav}>{open ? <ChevronLeftIcon /> : <MenuIcon />}</IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ p: 1, pt: 1.5, flexGrow: 1 }}>
          {MENU.map(({ to, label, icon, end }) => (
            <NavLink key={to} to={to} end={end} style={{ textDecoration: 'none' }}>
              {({ isActive }) => (
                <Tooltip title={open ? '' : label} placement='right'>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: open ? 'flex-start' : 'center', gap: 1.5, px: 1.5, py: 1.25, mb: 1, borderRadius: '6px', fontSize: 14, whiteSpace: 'nowrap', color: isActive ? '#fff' : '#3c3c3c', bgcolor: isActive ? 'primary.main' : 'transparent', '&:hover': { bgcolor: isActive ? 'primary.main' : '#f1f3f6' } }}>
                    <Badge color='error' badgeContent={to === '/platform/requests' ? openRequests : 0} max={99}>{icon}</Badge>{open && label}
                  </Box>
                </Tooltip>
              )}
            </NavLink>
          ))}
        </Box>
        <Box sx={{ p: open ? 1.5 : 1, borderTop: '1px solid #dcdcdc', textAlign: 'center' }}>
          {open ? (
            <>
              <h5 style={{ fontWeight: 'normal', color: '#000', fontSize: 13, margin: 0, wordBreak: 'break-word', textAlign: 'left' }}>{admin?.name}</h5>
              <h5 style={{ fontWeight: 'normal', color: '#6b7280', fontSize: 12, margin: '0 0 8px', wordBreak: 'break-word', textAlign: 'left' }}>{admin?.email}</h5>
              <Button size='small' variant='outlined' fullWidth startIcon={<LogoutIcon />} onClick={logout} sx={{ textTransform: 'none' }}>Sign out</Button>
            </>
          ) : (
            <Tooltip title={`Sign out${admin ? ` (${admin.email})` : ''}`} placement='right'>
              <IconButton size='small' color='primary' onClick={logout}><LogoutIcon fontSize='small' /></IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <PlatformHeader title={pageTitle} admin={admin} />
        <Box sx={{ p: 3 }}>
          <PageTitleContext.Provider value={setPageTitle}>
            <Outlet context={admin} />
          </PageTitleContext.Provider>
        </Box>
      </Box>
    </Box>
  );
};

export default PlatformLayout;
